import { LoaderBar } from '@components/LoaderBar';
import { StyledFormTitle } from '@elements/Title';
import { useScmPredictionGraph } from '@hooks/scm';
import SeasonalityChartBase from '@page-components/condensed-timeline/sales-forecast/SeasonalityChartBase';
import SeasonalityChartDeals from '@page-components/condensed-timeline/sales-forecast/SeasonalityChartDeals';
import SeasonalityChartLines from '@page-components/condensed-timeline/sales-forecast/SeasonalityChartLines';
import SeasonalityChartRegionSelect from '@page-components/condensed-timeline/sales-forecast/SeasonalityChartRegionSelect';
import SeasonalityChartSpikes from '@page-components/condensed-timeline/sales-forecast/SeasonalityChartSpikes';
import { SeasonalityEventForm } from '@page-components/condensed-timeline/sales-forecast/SeasonalityEventForm';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import {
  chartDisplayDate,
  getDatesBetween,
  getMarketData,
  lineChartOptions,
  parseChartValue,
  parseDate,
  parseDates,
} from '@utility/sales-forecast';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Legend, LineChart, ResponsiveContainer } from 'recharts';
import { cn } from '@skupreme/shared/ui';

function ContentWrapper({
  chartMargin = false,
  children,
}: {
  chartMargin?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'sk:relative sk:overflow-hidden sk:rounded-[5px]',
        'sk:[&_.recharts-legend-wrapper]:top-[15px]! sk:[&_.recharts-legend-wrapper]:right-[15px]!',
        'sk:[&_.recharts-text.recharts-label]:text-fg-tertiary!',
        chartMargin && 'sk:[&_svg.recharts-surface]:-mt-0.5',
      )}
    >
      {children}
    </div>
  );
}

function ChartTitle({
  top,
  children,
}: {
  top: 'comparison' | 'margin';
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'sk:absolute sk:left-[25px] sk:z-[1] sk:text-[15px] sk:leading-normal sk:font-bold sk:text-primary',
        top === 'comparison' && 'sk:-top-0.5',
        top === 'margin' && 'sk:top-9',
      )}
    >
      {children}
    </div>
  );
}

/*
 * SeasonalityChartEventLine marks the hovered event's group `visible` and the
 * chart `event-hover`; the other events' labels hide meanwhile.
 */
const CHART_WRAPPER = cn(
  'sk:bg-page',
  'sk:[&_.recharts-wrapper.event-hover_.recharts-layer.recharts-reference-line_g:not(.visible)]:invisible',
  'sk:[&_foreignObject]:overflow-hidden sk:[&_foreignObject]:whitespace-nowrap sk:[&_foreignObject]:transition-all sk:[&_foreignObject]:duration-250 sk:[&_foreignObject]:ease-[ease]',
  'sk:[&_path]:overflow-hidden sk:[&_path]:whitespace-nowrap sk:[&_path]:transition-all sk:[&_path]:duration-250 sk:[&_path]:ease-[ease]',
);

const showEventTitle = formData => {
  const { name: eventName } = formData || {};
  let title = eventName || '';
  if (title.length > 30) {
    title = `${title.slice(0, 30)}...`;
  }
  return title ? `“${title}”` : '';
};

export function SeasonalityChart(props) {
  const {
    addEventCount,
    chartType,
    itemId,
    lightningDeal,
    marketplaceDropdown = [],
    marketplaceRegionId: regionId,
    refreshTimeline,
    regionalItem,
    regionalItemsList = [],
    regions,
    seasonalSpike,
    setAddEventCount,
    upc,
    refreshKey,
  } = props;

  const [excludedRegions, setExcludedRegions] = useState(
    (marketplaceDropdown || [])
      .filter(d => d.value !== regionalItem)
      .map(d => d.name),
  );
  const [eventId, setEventId] = useState('');
  const [formData, setFormData] = useState(null);

  const {
    isLoading: isChartLoading,
    data: chartData,
    refreshData,
  } = useScmPredictionGraph(upc, refreshKey);

  const seasonalSpikeDates = useMemo(
    () => parseDates(seasonalSpike),
    [seasonalSpike],
  );
  const lightningDealDates = useMemo(
    () => parseDates(lightningDeal),
    [lightningDeal],
  );

  const { lightningIdMap } = useMemo(() => {
    const ligthnings = {};
    lightningDeal?.forEach(deal => {
      if (!ligthnings[deal.marketplace_region_id])
        ligthnings[deal.marketplace_region_id] = {};

      ligthnings[deal._id] = deal;
    });

    return {
      lightningIdMap: ligthnings,
    };
  }, [lightningDeal, seasonalSpike]);

  const showForm = useMemo(
    () => addEventCount > 0 || !!eventId,
    [addEventCount, eventId],
  );

  const chartHeight = useMemo(
    () => lineChartOptions.height * (showForm ? 0.75 : 1),
    [lineChartOptions, showForm],
  );

  const getTrueBase = marketplacePoint => {
    let count = Number(marketplacePoint?.count);
    marketplacePoint?.lightningDeals?.forEach(lightning => {
      const totalDays = DateUtility.difference(
        lightningIdMap?.[lightning._id]?.from,
        lightningIdMap?.[lightning._id]?.to,
      );
      const toMinus = lightning.units / totalDays;
      count -= toMinus;
    });

    let percentageAdded = 0;
    marketplacePoint?.seasonalSpikes?.forEach(season => {
      percentageAdded += season.percentage;
    });

    percentageAdded /= 100;
    if (percentageAdded) {
      count /= percentageAdded;
    }

    return count;
  };

  const getCountOfCurrentNode = (event, base, eventId, otherDeals) => {
    let count = Number(base);
    let unit = Number(event.units);

    let percentageAdded = 0;

    otherDeals?.seasonalSpikes?.forEach(season => {
      if (season._id !== eventId) {
        percentageAdded += season.percentage;
      }
    });

    if (event.type === 'percentage') {
      unit += 100;
      percentageAdded += unit;
    }

    percentageAdded /= 100;

    if (percentageAdded) {
      count *= percentageAdded;
    }

    otherDeals?.lightningDeals?.forEach(lightning => {
      if (lightning._id !== eventId) {
        const totalDays = DateUtility.difference(
          lightningIdMap[lightning._id].from,
          lightningIdMap[lightning._id].to,
        );
        const toAdd = lightning.units / totalDays;
        count += toAdd;
      }
    });

    if (event.type === 'units') {
      const totalDays = DateUtility.difference(event.from, event.to);
      const toAdd = unit / totalDays;
      count += toAdd;
    }
    if (count < 0) {
      return 0;
    }

    return count;
  };

  const dateRange = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = `0${new Date().getMonth() + 1}`.slice(-2);
    const parsedToday = DateUtility.formatDate(
      new Date(),
      DateFormat.yearMonthDate,
    );

    if (chartType === 'YEAR_TO_DATE') {
      return {
        startDate: `${currentYear}-01-01`,
        endDate: parsedToday,
      };
    }

    if (chartType === 'MONTH_TO_DATE') {
      return {
        startDate: `${currentYear}-${currentMonth}-01`,
        endDate: parsedToday,
      };
    }

    if (chartType === 'YEARLY') {
      const endDate = new Date(new Date().setFullYear(currentYear + 1));

      return {
        startDate: parsedToday,
        endDate: DateUtility.formatDate(endDate, DateFormat.yearMonthDate),
      };
    }

    if (chartType === 'MONTHLY') {
      const endDate = new Date(new Date().setMonth(new Date().getMonth() + 1));

      return {
        startDate: parsedToday,
        endDate: DateUtility.formatDate(endDate, DateFormat.yearMonthDate),
      };
    }

    if (chartType === 'WEEKLY') {
      const endDate = new Date(new Date().setDate(new Date().getDate() + 7));

      return {
        startDate: parsedToday,
        endDate: DateUtility.formatDate(endDate, DateFormat.yearMonthDate),
      };
    }

    return {
      startDate: '',
      endDate: '',
    };
  }, [chartType]);

  const { ids: marketplaceRegionIds, names: marketplaceRegionNames } =
    useMemo(() => {
      const regionNames = marketplaceDropdown.map(d => d.name);

      const ids = [];
      const names = {};

      regionalItemsList.forEach(m => {
        names[m.marketplace_region_id] = m.marketplace_region_name;
        if (regionNames.includes(m.marketplace_region_name)) {
          // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
          ids.push(m.marketplace_region_id);
        }
      });

      return { ids, names };
    }, [marketplaceDropdown, regionalItemsList]);

  const mappedData = useMemo(() => {
    const {
      globalFuturePrediction = [],
      globalSalesData = [],
      regionalFuturePrediction = [],
      regionalSalesData = {},
    } = chartData || {};

    const regionData = {};
    marketplaceRegionIds.forEach(id => {
      const futureMarket = getMarketData(regionalFuturePrediction, id);
      const pastMarket = getMarketData(regionalSalesData, id);
      // @ts-expect-error - TS2322: Type 'any[]' is not assignable to type 'never'.
      regionData[id] = [...pastMarket, ...futureMarket];
    });

    const mappedData = {};
    const regionDataMap = {};

    CommonUtility.isValidArray(globalFuturePrediction) &&
      globalFuturePrediction.forEach(d => {
        // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
        if (mappedData[parseDate(d.date)] === undefined)
          // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
          mappedData[parseDate(d.date)] = {};

        // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
        mappedData[parseDate(d.date)].global = parseChartValue(d?.average);
      });

    CommonUtility.isValidArray(globalSalesData) &&
      globalSalesData.forEach(d => {
        // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
        if (mappedData[parseDate(d.date)] === undefined)
          // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
          mappedData[parseDate(d.date)] = {};

        // @ts-expect-error - TS2339: Property 'date' does not exist on type 'never'.
        mappedData[parseDate(d.date)].past = parseChartValue(d?.count);
      });

    Object.keys(regionData).map(id => {
      if (!regionDataMap[id]) regionDataMap[id] = {};

      CommonUtility.isValidArray(regionData[id]) &&
        regionData[id].forEach(d => {
          // @ts-expect-error - TS18046: 'd' is of type 'unknown'.
          if (mappedData[parseDate(d.date)] === undefined)
            // @ts-expect-error - TS18046: 'd' is of type 'unknown'.
            mappedData[parseDate(d.date)] = {};

          // @ts-expect-error - TS18046: 'd' is of type 'unknown'.
          regionDataMap[id][parseDate(d.date)] = d;
          // @ts-expect-error - TS18046: 'd' is of type 'unknown'.
          mappedData[parseDate(d.date)][id] = parseChartValue(d?.count);
        });
    });

    const baseChartData = {};
    const comparisonChartData = {};
    const mainChartData = {};

    const formDates =
      // @ts-expect-error - TS2339: Property 'from' does not exist on type 'never'.
      formData?.from && formData?.to
        ? // @ts-expect-error - TS2339: Property 'from' does not exist on type 'never'.
          getDatesBetween(formData?.from, formData?.to).map(d => parseDate(d))
        : [];

    // @ts-expect-error - TS2339: Property 'country' does not exist on type 'never'.
    const id = formData?.country;
    const formMarketplaceRegionId = (regionalItemsList || []).find(
      x => x._id === id || x.marketplace_region_id === id,
    )?.marketplace_region_id;

    getDatesBetween(dateRange?.startDate, dateRange?.endDate).forEach(d => {
      const key = parseDate(d);
      if (key.includes('02-29') && !mappedData[key]) {
        return;
      }
      const pastDateObject = new Date(
        new Date(d).setFullYear(new Date(d).getFullYear() - 1),
      );

      const futureDate = parseDate(d);
      const pastDate = parseDate(pastDateObject);

      baseChartData[key] = {
        name: chartDisplayDate(d),
        pastDate: chartDisplayDate(pastDate),
        futureDate: chartDisplayDate(futureDate),
        past: mappedData?.[pastDate]?.past ?? 0,
        global: mappedData?.[futureDate]?.global ?? 0,
      };

      comparisonChartData[key] = {
        name: chartDisplayDate(d),
        pastDate: chartDisplayDate(pastDate),
        futureDate: chartDisplayDate(futureDate),
        past: mappedData?.[pastDate]?.past ?? 0,
        global: 0,
      };

      mainChartData[key] = {
        name: chartDisplayDate(d),
        pastDate: chartDisplayDate(pastDate),
        futureDate: chartDisplayDate(futureDate),
        past: mappedData?.[pastDate]?.past ?? 0,
        global: mappedData?.[futureDate]?.global ?? 0,
      };

      marketplaceRegionIds.forEach(id => {
        let futureRegionData = mappedData?.[futureDate]?.[id] ?? 0;

        const pastRegionData = mappedData?.[pastDate]?.[id] ?? 0;
        baseChartData[key][id] = pastRegionData;

        if (futureRegionData) {
          baseChartData[key][id] = futureRegionData;
        }

        if (
          showForm &&
          formMarketplaceRegionId === id &&
          // @ts-expect-error - TS2339: Property 'units' does not exist on type 'never'.
          !isNaN(formData?.units) &&
          // @ts-expect-error - TS2339: Property 'units' does not exist on type 'never'.
          Number(formData?.units) > 0 &&
          // @ts-expect-error - TS2339: Property 'type' does not exist on type 'never'.
          formData?.type &&
          formDates.includes(key)
        ) {
          const base = getTrueBase(regionDataMap[id][key]);

          const newCount = getCountOfCurrentNode(
            formData,
            base,
            eventId,
            regionDataMap[id][key],
          );
          futureRegionData = newCount;
        }

        comparisonChartData[key][id] = futureRegionData;
        comparisonChartData[key].global += futureRegionData;

        mainChartData[key][id] = futureRegionData;
      });
    });

    return {
      baseChart: Object.values(baseChartData),
      comparisonChart: Object.values(comparisonChartData),
      mainChart: Object.values(mainChartData),
    };
  }, [
    chartData,
    dateRange?.endDate,
    dateRange?.startDate,
    excludedRegions,
    // @ts-expect-error - TS2339: Property 'country' does not exist on type 'never'.
    formData?.country,
    // @ts-expect-error - TS2339: Property 'from' does not exist on type 'never'.
    formData?.from,
    // @ts-expect-error - TS2339: Property 'to' does not exist on type 'never'.
    formData?.to,
    // @ts-expect-error - TS2339: Property 'type' does not exist on type 'never'.
    formData?.type,
    // @ts-expect-error - TS2339: Property 'units' does not exist on type 'never'.
    formData?.units,
    marketplaceDropdown,
    marketplaceRegionIds,
    regionId,
    showForm,
  ]);

  useEffect(() => {
    if (!eventId) {
      setFormData(null);
    }
  }, [eventId]);

  const dataChartContent = useMemo(
    () => (showForm ? mappedData.baseChart : mappedData.mainChart),
    [showForm, mappedData],
  );

  const showComparisonChart = useMemo(
    () => showForm && CommonUtility.isValidArray(mappedData?.comparisonChart),
    [mappedData, showForm],
  );

  const isChartContentLoading = useMemo(
    () => isChartLoading || !CommonUtility.isValidArray(dataChartContent),
    [isChartLoading, dataChartContent],
  );

  return (
    <div className="sk:flex sk:flex-wrap sk:-mx-4">
      {showForm && (
        <div className="sk:w-4/12 sk:flex-none sk:px-4">
          <div className="sk:flex! sk:flex-col! sk:justify-center! sk:h-full sk:min-[992px]:mt-1! sk:min-[992px]:mb-10!">
            <StyledFormTitle className="sk:mb-5!">
              {eventId ? 'Edit' : 'New'} Event
            </StyledFormTitle>
            <SeasonalityEventForm
              key={eventId}
              eventId={eventId}
              forceRefreshSales={refreshData}
              itemId={itemId}
              lightningDeal={lightningDeal}
              marketplaceDropdown={marketplaceDropdown}
              marketplaceRegionId={regionId}
              refreshTimeline={refreshTimeline}
              regionId={regionalItem}
              regionalItemsList={regionalItemsList}
              seasonalSpike={seasonalSpike}
              setAddEventCount={setAddEventCount}
              setEventId={setEventId}
              setFormData={setFormData}
            />
          </div>
        </div>
      )}
      <div
        className={
          showForm
            ? 'sk:w-8/12 sk:flex-none sk:px-4 sk:flex! sk:flex-col!'
            : 'sk:w-full sk:flex-none sk:px-4'
        }
      >
        {isChartContentLoading && <LoaderBar />}
        {CommonUtility.isValidArray(dataChartContent) && (
          <ContentWrapper chartMargin={showForm}>
            <SeasonalityChartRegionSelect
              excludedRegions={excludedRegions}
              marketplaceDropdown={marketplaceDropdown}
              regionalItem={regionalItem}
              regions={regions}
              setExcludedRegions={setExcludedRegions}
            />
            {showForm && <ChartTitle top="margin">Base Forecast</ChartTitle>}
            <ResponsiveContainer
              className={CHART_WRAPPER}
              height={chartHeight}
              width="100%"
            >
              <LineChart
                {...lineChartOptions}
                height={chartHeight}
                data={dataChartContent}
              >
                {/*
                 * Note: Custom Components Are Not Working With Recharts Library Thus Need To Call Components Like Functions
                 * Reference Solution From Open Issue: https://github.com/recharts/recharts/issues/2788#issuecomment-1352589299
                 */}
                {SeasonalityChartBase()}
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="plainline"
                  iconSize={20}
                />
                {SeasonalityChartLines({
                  excludedRegions,
                  marketplaceRegionIds,
                  marketplaceRegionNames,
                })}
                {!showComparisonChart &&
                  SeasonalityChartSpikes({
                    eventId,
                    seasonalSpike,
                    seasonalSpikeDates,
                    setEventId,
                  })}
                {!showComparisonChart &&
                  SeasonalityChartDeals({
                    eventId,
                    lightningDeal,
                    lightningDealDates,
                    setEventId,
                  })}
              </LineChart>
            </ResponsiveContainer>
          </ContentWrapper>
        )}
        {showComparisonChart && (
          <ContentWrapper>
            <ChartTitle top="comparison">
              Forecast with {showEventTitle(formData)} Event
            </ChartTitle>
            <ResponsiveContainer
              className={CHART_WRAPPER}
              height={chartHeight}
              width="100%"
            >
              <LineChart
                {...lineChartOptions}
                height={chartHeight}
                data={mappedData?.comparisonChart}
              >
                {SeasonalityChartBase()}
                {SeasonalityChartLines({
                  excludedRegions,
                  marketplaceRegionIds,
                  marketplaceRegionNames,
                })}
                {SeasonalityChartSpikes({
                  eventId,
                  seasonalSpike,
                  seasonalSpikeDates,
                  setEventId,
                })}
                {SeasonalityChartDeals({
                  eventId,
                  lightningDeal,
                  lightningDealDates,
                  setEventId,
                })}
              </LineChart>
            </ResponsiveContainer>
          </ContentWrapper>
        )}
      </div>
    </div>
  );
}

export default SeasonalityChart;
